import { datadogRum } from "@datadog/browser-rum";
import { useFeatureValue } from "@growthbook/growthbook-react";

import { useVeroGrowthBook } from "./growthbook";
import { VeroFlag, DEFAULT_FLAG_VALUES } from "./veroFlags";

/**
 * Hook for accessing a single feature flag
 * Automatically re-renders when the flag changes via streaming
 * Uses GrowthBook's built-in useFeatureValue hook for automatic updates
 * Returns default value when GrowthBook is not ready
 *
 * @param {VeroFlag} flagKey - The feature flag key from VeroFlag enum
 * @param {boolean} defaultValue - Default value if flag is not available (optional, uses DEFAULT_FLAG_VALUES if not provided)
 * @returns {boolean} The flag value
 *
 * @example
 * function MyComponent() {
 *   const isAttachmentsRedesign = useVeroFlag(VeroFlag.useAttachmentsRedesign)
 *   return isAttachmentsRedesign ? <NewUI /> : <OldUI />
 * }
 *
 * For location-based evaluation, use useSetVeroFlagLocation first.
 */
export const useVeroFlag = (
  flagKey: VeroFlag,
  defaultValue?: boolean
): boolean => {
  const gb = useVeroGrowthBook();
  const finalDefault = defaultValue ?? DEFAULT_FLAG_VALUES[flagKey];

  // Use GrowthBook's built-in useFeatureValue hook which automatically subscribes to changes
  // useFeatureValue supports any type of value, not just booleans, for future flexibility
  const value = useFeatureValue(flagKey, finalDefault);

  // If GrowthBook is not ready, explicitly return default to be safe
  const finalValue = !gb || !gb.ready ? finalDefault : value;

  // Track evaluation in Datadog
  datadogRum.addFeatureFlagEvaluation(flagKey, finalValue);

  return finalValue;
};
